// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Sheet from '@app/features/ui/sheet/Sheet';
import {observer} from 'mobx-react-lite';
import type React from 'react';

interface BottomSheetProps {
	fitContent?: boolean;
	pinToBottom?: boolean;
	isOpen: boolean;
	onClose: () => void;
	children: React.ReactNode;
	title?: string;
	initialSnap?: number;
	snapPoints?: Array<number>;
	disablePadding?: boolean;
	disableDefaultHeader?: boolean;
	zIndex?: number;
	showHandle?: boolean;
	showCloseButton?: boolean;
	/** The line under the title; off when the content's first row sits right under it. */
	headerBorder?: boolean;
	surface?: 'primary' | 'secondary' | 'tertiary';
	headerSlot?: React.ReactNode;
	leadingAction?: React.ReactNode;
	trailingAction?: React.ReactNode;
	containerClassName?: string;
	contentClassName?: string;
	/** Inset from the edges on the theme's lens material, as action menus are. */
	floating?: boolean;
}

export const BottomSheet: React.FC<BottomSheetProps> = observer(
	({
		isOpen,
		onClose,
		children,
		title,
		initialSnap = 1,
		snapPoints = [0, 0.5, 0.8, 1],
		disablePadding = false,
		disableDefaultHeader = false,
		zIndex,
		showHandle = true,
		showCloseButton = true,
		headerBorder = true,
		surface = 'secondary',
		fitContent = false,
		pinToBottom = false,
		headerSlot,
		leadingAction,
		trailingAction,
		containerClassName,
		contentClassName,
		floating = false,
	}) => {
		const shouldRenderDefaultHeader =
			!disableDefaultHeader && (!!title || !!leadingAction || !!trailingAction || showCloseButton);
		const renderTrailingContent = () => {
			if (!shouldRenderDefaultHeader) return undefined;
			if (trailingAction && showCloseButton) {
				return (
					<>
						{trailingAction}
						<Sheet.CloseButton
							onClick={onClose}
							data-flx="ui.bottom-sheet.bottom-sheet.render-trailing-content.sheet-close-button"
						/>
					</>
				);
			}
			if (showCloseButton) {
				return (
					<Sheet.CloseButton
						onClick={onClose}
						data-flx="ui.bottom-sheet.bottom-sheet.render-trailing-content.sheet-close-button--2"
					/>
				);
			}
			return trailingAction;
		};
		return (
			<Sheet.Root
				isOpen={isOpen}
				onClose={onClose}
				snapPoints={snapPoints}
				initialSnap={initialSnap}
				surface={surface}
				fitContent={fitContent}
				floating={floating}
				pinToBottom={pinToBottom}
				zIndex={zIndex}
				className={containerClassName}
				data-flx="ui.bottom-sheet.bottom-sheet.sheet-root"
			>
				{showHandle && <Sheet.Handle data-flx="ui.bottom-sheet.bottom-sheet.sheet-handle" />}
				{shouldRenderDefaultHeader && (
					<Sheet.Header
						leading={leadingAction}
						trailing={renderTrailingContent()}
						safeAreaTop={!showHandle}
						border={headerBorder}
						after={headerSlot}
						data-flx="ui.bottom-sheet.bottom-sheet.sheet-header"
					>
						{title && <Sheet.Title data-flx="ui.bottom-sheet.bottom-sheet.sheet-title">{title}</Sheet.Title>}
					</Sheet.Header>
				)}
				{!shouldRenderDefaultHeader && headerSlot}
				{disablePadding ? (
					children
				) : (
					<Sheet.Content className={contentClassName} data-flx="ui.bottom-sheet.bottom-sheet.sheet-content">
						{children}
					</Sheet.Content>
				)}
			</Sheet.Root>
		);
	},
);
